import { Route, Router } from "@angular/router";

import { notExternalOrParticipatingEntity, pmoEntity } from "../../core/guards";
import { SynchronizationSchedulePageType } from "../../shared/constants/enums";
import { khadamatiPagesGuards, syncLogsPageGuards, syncModuleGuards, syncPagesGuards } from "./_guards";

const KHADAMATI_ROUTES: Route[] = [
  {
    path: "khadamati",
    canActivate: [pmoEntity, ...khadamatiPagesGuards],
    children: [
      {
        path: "",
        loadComponent: () => import("../../shared/components").then((e) => e.SubmenuComponent),
        data: {
          breadcrumb: "breadcrumbs.sync_khadamati",
          title: "breadcrumbs.sync_khadamati",
          onEntityChange: (router: Router) => router.navigateByUrl("console"),
        },
      },
      {
        path: "logs",
        loadComponent: () =>
          import("./synchronization-log/synchronization-log").then((e) => e.SynchronizationLogComponent),
        data: {
          breadcrumb: "breadcrumbs.khadamati.list",
          title: "breadcrumbs.khadamati.list",
          pageType: SynchronizationSchedulePageType.KHADAMATI,
        },
      },
      {
        path: "logs/:selectedLogId",
        loadComponent: () =>
          import("./synchronization-log/synchronization-log").then((e) => e.SynchronizationLogComponent),
        data: {
          breadcrumb: "breadcrumbs.khadamati.list",
          title: "breadcrumbs.khadamati.list",
          pageType: SynchronizationSchedulePageType.KHADAMATI,
        },
      },
      {
        path: "schedule",
        loadComponent: () =>
          import("./synchronization-schedule/synchronization-schedule").then((e) => e.SynchronizationScheduleComponent),
        data: {
          breadcrumb: "breadcrumbs.khadamati.schedule",
          title: "breadcrumbs.khadamati.schedule",
          pageType: SynchronizationSchedulePageType.KHADAMATI,
        },
      },
    ],
  },
];

const DIMENSIONS_SYNC_ROUTES: Route[] = [
  {
    path: "dimensions",
    children: [
      {
        path: "",
        loadComponent: () => import("../../shared/components").then((e) => e.SubmenuComponent),
        data: {
          breadcrumb: "breadcrumbs.sync_dimensions",
          title: "breadcrumbs.sync_dimensions",
          onEntityChange: (router: Router) => router.navigateByUrl("console"),
        },
      },
      {
        path: "logs",
        loadComponent: () =>
          import("./synchronization-log/synchronization-log").then((e) => e.SynchronizationLogComponent),
        canActivate: [...syncLogsPageGuards],
        data: {
          breadcrumb: "dimension.sync_logs",
          title: "dimension.sync_logs",
          pageType: SynchronizationSchedulePageType.DIMENSIONS,
        },
      },
      {
        path: "logs/:selectedLogId",
        loadComponent: () =>
          import("./synchronization-log/synchronization-log").then((e) => e.SynchronizationLogComponent),
        canActivate: [...syncLogsPageGuards],
        data: {
          breadcrumb: "dimension.sync_logs",
          title: "dimension.sync_logs",
          pageType: SynchronizationSchedulePageType.DIMENSIONS,
        },
      },
      {
        path: "schedule",
        loadComponent: () =>
          import("./synchronization-schedule/synchronization-schedule").then((e) => e.SynchronizationScheduleComponent),
        data: {
          breadcrumb: "dimension.sync_schedule",
          title: "dimension.sync_schedule",
          pageType: SynchronizationSchedulePageType.DIMENSIONS,
        },
      },
    ],
  },
];

const ENTITY_DATA_SYNC_ROUTES: Route[] = [
  {
    path: "entity-data",
    children: [
      {
        path: "",
        loadComponent: () => import("../../shared/components").then((e) => e.SubmenuComponent),
        data: {
          breadcrumb: "breadcrumbs.sync_entity_data",
          title: "breadcrumbs.sync_entity_data",
          onEntityChange: (router: Router) => router.navigateByUrl("console"),
        },
      },
      {
        path: "logs",
        loadComponent: () =>
          import("./synchronization-log/synchronization-log").then((e) => e.SynchronizationLogComponent),
        canActivate: [pmoEntity, ...syncLogsPageGuards],
        data: {
          breadcrumb: "dimension.entity_data.sync_logs",
          title: "dimension.entity_data.sync_logs",
          pageType: SynchronizationSchedulePageType.ENTITY_DATA,
        },
      },
      {
        path: "logs/:selectedLogId",
        loadComponent: () =>
          import("./synchronization-log/synchronization-log").then((e) => e.SynchronizationLogComponent),
        canActivate: [pmoEntity, ...syncLogsPageGuards],
        data: {
          breadcrumb: "dimension.entity_data.sync_logs",
          title: "dimension.entity_data.sync_logs",
          pageType: SynchronizationSchedulePageType.ENTITY_DATA,
        },
      },
      {
        path: "configured-schedulers",
        canActivate: [pmoEntity, ...syncPagesGuards],
        loadComponent: () =>
          import("./configured-scheduler-list/configured-scheduler-list").then(
            (e) => e.ConfiguredSchedulerListComponent
          ),
        data: {
          breadcrumb: "dimension.entity_data.configured_schedulers",
          title: "dimension.entity_data.configured_schedulers",
        },
      },
    ],
  },
];

const ORG_UNIT_ROUTES: Route[] = [
  {
    path: "org-unit",
    canActivate: [pmoEntity, ...syncPagesGuards],
    children: [
      {
        path: "",
        loadComponent: () => import("../../shared/components").then((e) => e.SubmenuComponent),
        data: {
          breadcrumb: "breadcrumbs.sync_org_unit",
          title: "breadcrumbs.sync_org_unit",
          onEntityChange: (router: Router) => router.navigateByUrl("console"),
        },
      },
      {
        path: "logs",
        loadComponent: () =>
          import("./synchronization-log/synchronization-log").then((e) => e.SynchronizationLogComponent),
        data: {
          breadcrumb: "dimension.entity_data.org_unit_logs",
          title: "dimension.entity_data.org_unit_logs",
          pageType: SynchronizationSchedulePageType.ORG_UNIT,
        },
      },
      {
        path: "logs/:selectedLogId",
        loadComponent: () =>
          import("./synchronization-log/synchronization-log").then((e) => e.SynchronizationLogComponent),
        data: {
          breadcrumb: "dimension.entity_data.org_unit_logs",
          title: "dimension.entity_data.org_unit_logs",
          pageType: SynchronizationSchedulePageType.ORG_UNIT,
        },
      },
      {
        path: "schedule",
        loadComponent: () =>
          import("./synchronization-schedule/synchronization-schedule").then((e) => e.SynchronizationScheduleComponent),
        data: {
          breadcrumb: "dimension.entity_data.org_unit_schedule",
          title: "dimension.entity_data.org_unit_schedule",
          pageType: SynchronizationSchedulePageType.ORG_UNIT,
        },
      },
    ],
  },
];

export const SYNC_ROUTES: Route[] = [
  {
    path: "sync",
    canActivate: [notExternalOrParticipatingEntity, ...syncModuleGuards],
    children: [
      {
        path: "",
        loadComponent: () => import("../../shared/components").then((e) => e.SubmenuComponent),
        data: {
          breadcrumb: "breadcrumbs.sync",
          title: "breadcrumbs.sync",
          onEntityChange: (router: Router) => router.navigateByUrl("console"),
        },
      },
      ...KHADAMATI_ROUTES,
      ...DIMENSIONS_SYNC_ROUTES,
      ...ENTITY_DATA_SYNC_ROUTES,
      ...ORG_UNIT_ROUTES,
    ],
  },
];
